import type { IconSequenceParams, IconSequenceState } from '../types';

interface Props {
  params: IconSequenceParams;
  puzzleState: IconSequenceState;
  onSolve: () => void;
  patchState: (updater: (prev: IconSequenceState) => IconSequenceState) => void;
}

export function IconSequenceLock({ params, puzzleState, onSolve, patchState }: Props) {
  const { slots, palette, correctSequence, clueText } = params;
  const { sequence, solved, wrong } = puzzleState;

  function placeIcon(icon: string) {
    if (solved || wrong) return;
    patchState(s => {
      const nextSeq = [...s.sequence];
      const emptyIdx = nextSeq.findIndex(v => v === null);
      if (emptyIdx === -1) return s; // all slots filled, need to submit first
      nextSeq[emptyIdx] = icon;

      // Auto-check when all filled
      if (emptyIdx === slots - 1) {
        const correct = correctSequence.every((c, i) => nextSeq[i] === c);
        if (correct) {
          onSolve();
          return { ...s, sequence: nextSeq, solved: true, wrong: false };
        } else {
          // show wrong, then clear
          setTimeout(() => {
            patchState(ss => ({ ...ss, wrong: false, sequence: Array(slots).fill(null) }));
          }, 900);
          return { ...s, sequence: nextSeq, wrong: true };
        }
      }
      return { ...s, sequence: nextSeq };
    });
  }

  function clearSlot(idx: number) {
    if (solved || wrong) return;
    patchState(s => {
      const nextSeq = [...s.sequence];
      nextSeq[idx] = null;
      return { ...s, sequence: nextSeq };
    });
  }

  if (solved) {
    return (
      <p className="puzzle-solved-msg">
        <i className="fa-solid fa-circle-check" aria-hidden="true" /> Access granted.
      </p>
    );
  }

  return (
    <div className="puzzle-sequence">
      <p className="puzzle-clue-text">{clueText}</p>
      <p className="puzzle-sequence__instruction">
        Click icons from the palette to fill the {slots} slots in order. Click a filled slot to remove it.
      </p>

      {/* Slots */}
      <div className={`puzzle-sequence__slots${wrong ? ' puzzle-sequence__slots--wrong' : ''}`}>
        {Array.from({ length: slots }, (_, i) => (
          <button
            key={i}
            className={`puzzle-sequence__slot${sequence[i] ? ' puzzle-sequence__slot--filled' : ''}`}
            type="button"
            aria-label={sequence[i] ? `Slot ${i + 1}: ${sequence[i]} (click to remove)` : `Slot ${i + 1}: empty`}
            onClick={() => sequence[i] && clearSlot(i)}
          >
            {sequence[i]
              ? <i className={`fa-solid fa-${sequence[i]}`} aria-hidden="true" />
              : <span className="puzzle-sequence__slot-num">{i + 1}</span>}
          </button>
        ))}
      </div>

      {wrong && (
        <p className="puzzle-error-msg">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> Wrong sequence — try again.
        </p>
      )}

      {/* Palette */}
      <div className="puzzle-sequence__palette">
        {palette.map(icon => (
          <button
            key={icon}
            className="puzzle-sequence__palette-btn"
            type="button"
            aria-label={`Add ${icon}`}
            onClick={() => placeIcon(icon)}
            disabled={sequence.includes(icon)}
          >
            <i className={`fa-solid fa-${icon}`} aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );
}
